import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export const DEFAULT_TOAST_DURATION_MS = 1500;

export interface Toast {
  id: number;
  type: ToastType;
  title: string;
  message: string;
  duration: number;
  /** When set, clicking the toast body navigates (close button still dismisses only). */
  actionRoute?: string | null;
}

export interface ToastShowOptions {
  duration?: number;
  actionRoute?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  toasts = signal<Toast[]>([]);
  private nextId = 1;

  show(
    type: ToastType,
    title: string,
    message: string,
    durationOrOptions: number | ToastShowOptions = DEFAULT_TOAST_DURATION_MS,
  ) {
    const options: ToastShowOptions =
      typeof durationOrOptions === 'number'
        ? { duration: durationOrOptions }
        : durationOrOptions;

    const id = this.nextId++;
    const duration = options.duration ?? DEFAULT_TOAST_DURATION_MS;
    const toast: Toast = {
      id,
      type,
      title,
      message,
      duration,
      actionRoute: options.actionRoute ?? null,
    };

    this.toasts.update((t) => [...t, toast]);
    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }
  }

  success(title: string, message: string, duration?: number) {
    this.show('success', title, message, duration);
  }

  error(title: string, message: string, duration?: number) {
    this.show('error', title, message, duration);
  }

  info(title: string, message: string, duration?: number) {
    this.show('info', title, message, duration);
  }

  warning(title: string, message: string, durationOrOptions?: number | ToastShowOptions) {
    this.show('warning', title, message, durationOrOptions ?? DEFAULT_TOAST_DURATION_MS);
  }

  remove(id: number) {
    this.toasts.update((t) => t.filter((x) => x.id !== id));
  }
}
